import { Controller } from "@hotwired/stimulus"
import { generateRequestId } from "../jsx/utils/requestId"
import { showError } from "../jsx/utils/notifications"

// EVA-311. The Export Report button, and the overlay you watch while it prints.
//
// Asks the server for a report, polls until it is ready, then downloads it. The
// stakeholder's requirement was literal: press the button and get the file — no
// preview screen, no browser print dialog. Chrome's print dialog applies its own
// default margins and the layout collapses, which is why the PDF is printed
// server-side and this controller never opens a print view.
//
// THE POLL BACKS OFF, and that is the difference between a fast export and one
// that feels slow. Printing is about a second: measured in production, 0.97 s
// for a small rent roll and 2.05 s for 342 tenants across 26 sheets. A flat
// 2000 ms poll therefore spent most of the wait asking nothing — a report ready
// at 0.97 s was still announced at 2 s, and one ready at 2.05 s waited until 4.
// The first check now lands at 250 ms and the interval grows from there, so the
// common case is a file in about a second.
//
// The give-up timer is not decoration. A Sidekiq restart between enqueue and
// perform leaves a run PENDING for ever, and without a deadline the button would
// spin silently until the tab is closed (the Value Bridge tab learned this).
export default class extends Controller {
  static values = {
    createUrl: String,
    statusUrl: String,
    downloadUrl: String,
    giveUpAfter: { type: Number, default: 300000 }
  }

  static targets = ["label", "button", "progress", "progressTitle", "progressHint"]

  // Tight at first because the answer usually arrives early, then wider so a
  // genuinely slow export is not polled sixty times a minute.
  static POLL_STEPS = [250, 300, 450, 700, 1000, 1500]
  static POLL_MAX = 2000

  // Long enough that a normal export never shows it, short enough to reassure
  // before anyone reaches for the tab close button.
  static SLOW_AFTER = 6000

  connect() {
    this.timer = null
    this.slowTimer = null
    this.startedAt = null
    this.runId = null
    this.attempt = 0
  }

  disconnect() {
    this.stopPolling()
  }

  async export(event) {
    event.preventDefault()
    if (this.timer) return

    this.startedAt = Date.now()
    this.attempt = 0
    this.setBusy(true)

    try {
      const response = await this.post(this.createUrlValue)
      const payload = await response.json()

      if (!payload.success) {
        this.fail(payload.error)
        return
      }

      this.runId = payload.run.id
      this.poll()
    } catch (error) {
      this.fail("unexpected_error")
    }
  }

  poll() {
    const wait = this.constructor.POLL_STEPS[this.attempt] ?? this.constructor.POLL_MAX
    this.attempt += 1

    this.timer = window.setTimeout(async () => {
      this.timer = null

      if (Date.now() - this.startedAt > this.giveUpAfterValue) {
        this.fail("run_abandoned")
        return
      }

      try {
        const response = await fetch(`${this.statusUrlValue}?run_id=${this.runId}`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin"
        })
        const payload = await response.json()
        const run = payload.run

        if (!run || run.status === "PENDING") {
          this.poll()
        } else if (run.status === "COMPLETE" && run.downloadable) {
          this.finish(run.id)
        } else {
          this.fail(run.error_code)
        }
      } catch (error) {
        this.fail("unexpected_error")
      }
    }, wait)
  }

  // Straight to the file. A "Report ready" card in between is a second screen
  // announcing what the download itself already says, and at half a second it
  // reads as a flicker rather than as confirmation.
  finish(runId) {
    this.stopPolling()
    this.setBusy(false)
    // A plain navigation rather than fetch + blob: the response is an
    // attachment, so the browser saves it and the page stays where it is.
    window.location.assign(`${this.downloadUrlValue}?run_id=${runId}`)
  }

  fail(code) {
    this.stopPolling()
    this.setBusy(false)
    // Codes, never messages — the server sends a code and the page owns the
    // wording (api.md).
    const message = this.element.dataset[`error${this.camelize(code)}`] ||
      this.element.dataset.errorUnexpectedError
    showError(message || code)
  }

  stopPolling() {
    if (this.timer) {
      window.clearTimeout(this.timer)
      this.timer = null
    }
    if (this.slowTimer) {
      window.clearTimeout(this.slowTimer)
      this.slowTimer = null
    }
  }

  setBusy(busy) {
    const button = this.hasButtonTarget ? this.buttonTarget : this.element
    button.classList.toggle("disabled", busy)
    button.setAttribute("aria-busy", busy ? "true" : "false")

    if (this.hasLabelTarget) {
      this.labelTarget.textContent = busy
        ? this.element.dataset.busyLabel
        : this.element.dataset.idleLabel
    }

    if (!this.hasProgressTarget) return

    if (busy) {
      this.resetProgress()
      this.progressTarget.hidden = false
      // Only after the export is genuinely dragging. Saying it sooner would be
      // reassurance nobody asked for.
      this.slowTimer = window.setTimeout(() => {
        if (this.hasProgressHintTarget) {
          this.progressHintTarget.textContent = this.element.dataset.progressSlow
        }
      }, this.constructor.SLOW_AFTER)
    } else {
      this.progressTarget.hidden = true
    }
  }

  resetProgress() {
    if (this.hasProgressTitleTarget) {
      this.progressTitleTarget.textContent = this.element.dataset.progressTitle
    }
    if (this.hasProgressHintTarget) {
      this.progressHintTarget.textContent = this.element.dataset.progressHint
    }
  }

  camelize(code) {
    return String(code || "unexpected_error")
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join("")
  }

  post(url) {
    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content ?? "",
        // Every write endpoint in this app carries one; without it the server
        // answers 400 missing_request_id.
        "X-Request-Id": generateRequestId()
      }
    })
  }
}
